import React from 'react';
import { Input, Button, Form, Card, message, Select, Tooltip, Modal } from 'antd';
import { FormInstance } from 'antd/lib/form';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import StatesSelect, { States, StatesSelectorType } from '../../components/public/StatesSelect';
import { ADD_CUSTOMER_SUCCESS_MESSAGE, ADD_CUSTOMER_ERROR_MESSAGE, GET_CUSTOMER_ERROR_MESSAGE, EDIT_CUSTOMER_SUCCESS_MESSAGE, EDIT_CUSTOMER_ERROR_MESSAGE, KEY_CUSTOMER_SELECTOR_HINT_MESSAGE, WRONG_MOBILE_NUMBER_MESSAGE } from '../../config/strcfg';
import './page-customer.less';
import CustomerService, { AddCustomerParam, EditCustomerParam, ICustomerInfo } from '../../service/customer/CustomerService';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ErrorBoundary from 'antd/lib/alert/ErrorBoundary';

interface CustomerManagerPageState {
  loading: boolean;
  isEditMode: boolean;
  customerData: ICustomerInfo;
  companyId: number | null;
}

class CustomerManagerPage extends React.Component<CustomerManagerPageState> {
  state : CustomerManagerPageState = {
    loading: false,
    isEditMode: false,
    customerData: null,
    companyId: null,
  };

  async componentDidMount() {
    //根据url判断展示添加or编辑页面模式
    const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const customerId = parseInt(searchParams.get('id'));
    const companyId = parseInt(searchParams.get('company'));
    if (companyId) {
        this.setState({ companyId });
    }

    //编辑用户页面模式
    if(customerId){
      this.setState({ isEditMode: true, loading: true });
      //编辑页面需要调用用户信息查询接口
      try{
        const response = await CustomerService.getCustomerById(customerId);
        if(response){
          const customerInfo: ICustomerInfo = response.data && response.data.customerInfo ? response.data.customerInfo : [];
          if(customerInfo){
            this.setState({ customerData: customerInfo, loading: false }); 
            
            // 在此处添加条件判断，确保customerData有值之后再设置value属性
            if (this.state.customerData) {
              this.formRef.current?.setFieldsValue(
                { 
                  cname: this.state.customerData.cname,
                  ename: this.state.customerData.ename,
                  gender: this.state.customerData.gender,
                  mobile: this.state.customerData.mobile,
                  phone: this.state.customerData.phone,
                  wechat: this.state.customerData.wechat,
                  email: this.state.customerData.email,
                  position: this.state.customerData.position,
                  department: this.state.customerData.department,
                  fax: this.state.customerData.fax,
                  keyCustomer: this.state.customerData.keyCustomer ? 1 : 0,
                  states: this.state.customerData.states,
                  remark: this.state.customerData.remark,
                });
            }
          }
          else{
            this.setState({ customerData: null });
            message.error(GET_CUSTOMER_ERROR_MESSAGE);
          }
        }
        else{
          this.setState({ customerData: null });
          message.error(GET_CUSTOMER_ERROR_MESSAGE);
        }
      } catch(error){
        message.error(GET_CUSTOMER_ERROR_MESSAGE);
      } finally{
        this.setState({ loading: false });
      }
    }
    //添加客户页面模式
    else{
      this.setState({ isEditMode: false });
    }
  }

  formRef = React.createRef<FormInstance>();

  //权限选择框事件处理(不写这个空方法会导致页面任何操作都会使RoleSelect执行useEffect的接口调用)
  handleRoleSelectData = () => {
  };

  // 返回按钮点击事件处理
    handleBackButtonClick = () => {
      window.history.back();
  };

  // 手机号验证
  validateMobileNumber = (mobile: string): boolean => {
    // 如果以+开头，允许任意位数的数字
    if (mobile.startsWith('+')) {
      return /^\+\d+$/.test(mobile);
    }
    // 如果不以+开头，必须是11位数字
    return /^\d{11}$/.test(mobile);
  };

  onFinish = async (params: any) => {
    // 手机号验证
    if (params.mobile && !this.validateMobileNumber(params.mobile)) {
      const confirmResult = await new Promise(resolve => {
        Modal.confirm({
          title: '提示',
          content: WRONG_MOBILE_NUMBER_MESSAGE,
          okText: '确认',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!confirmResult) {
        return;
      }
    }

    this.setState({ loading: true });
    // 调用addCustomer接口
    if (!this.state.isEditMode) {
        try {
            const response = await CustomerService.addCustomer({
                ...params,
                company: this.state.companyId, // 确保companyId被正确传递
            } as AddCustomerParam);
            if (response && response.success) {
                // 添加成功后的操作
                this.formRef.current?.resetFields();
                message.success(ADD_CUSTOMER_SUCCESS_MESSAGE);
            } else {
                // 添加失败后的操作
                message.error(response.message);
            }
        } catch (error) {
            message.error(ADD_CUSTOMER_ERROR_MESSAGE);
        } finally {
            this.setState({ loading: false });
        }
    }
    // 调用editCustomer接口
    else {
        try {
            const userId = this.state.customerData.id;
            const updatedParams = {
                ...params,
                id: userId,
                company: this.state.companyId, // 确保companyId被正确传递
            };
            const response = await CustomerService.updateCustomer(updatedParams as EditCustomerParam);
            if (response && response.success) {
                // 编辑成功后的操作
                this.formRef.current?.resetFields();
                message.success(EDIT_CUSTOMER_SUCCESS_MESSAGE);
                // 在提交表单后等待500ms再刷新页面，加载提交后的数据
                setTimeout(() => { window.location.reload(); }, 500);
            } else {
                // 编辑失败后的操作
                this.formRef.current?.resetFields();
                message.error(response.message);
                setTimeout(() => { window.location.reload(); }, 500);
            }
        } catch (error) {
            this.formRef.current?.resetFields();
            message.error(EDIT_CUSTOMER_ERROR_MESSAGE);
            setTimeout(() => { window.location.reload(); }, 500);
        } finally {
            this.setState({ loading: false });
        }
    }
  };

  render() {
    //form 初始值
    const initialValues = {
      states: this.state.customerData ? this.state.customerData.states : States.normal,
      gender: this.state.customerData ? this.state.customerData.gender : 0,
      keyCustomer: this.state.customerData ? (this.state.customerData.keyCustomer ? 1 : 0) : 0,
    };
    const cardTitle = this.state.isEditMode ? "编辑客户信息" : "添加客户信息"; 
    return (
      this.state.loading === false ?
        <ErrorBoundary>
          <div className="customer_page-container">
            <BreadcrumbCustom breads={['客户信息管理', this.state.isEditMode ? '编辑客户信息' : '添加客户信息']} />
            <Card 
              title={
                  <div className="title-customer_manager_page">
                      <Button type="link" className="back-button" onClick={this.handleBackButtonClick}>{'< 返回'}</Button>
                      {cardTitle}
                  </div>
              } 
              bordered={false}
            >
                <div className="add-customer_container">
                    <Form ref={this.formRef} onFinish={this.onFinish} initialValues={initialValues}>
                      <Form.Item label="姓名" name="cname" className="formItem-customer_required" rules={[{ required: true, message: '请输入客户姓名' }]}>
                        <Input placeholder="请输入客户姓名" autoComplete="off" />
                      </Form.Item>

                      <Form.Item label="英文名" name="ename" className="formItem-customer_required" rules={[{ required: false }]}>
                        <Input placeholder="请输入客户英文名" autoComplete="off" />
                      </Form.Item>

                      <Form.Item label="性别" name="gender" className="formItem-customer_selector" rules={[{ required: false }]}>
                        <Select>
                          <Select.Option key={'unknown'} value={0}>未知</Select.Option>
                          <Select.Option key={'male'} value={1}>男</Select.Option>
                          <Select.Option key={'female'} value={2}>女</Select.Option>
                        </Select>
                      </Form.Item>

                      <Form.Item label="手机号" name="mobile" className="formItem-customer_required" 
                                 rules={[{ required: true, message: '请输入客户手机号' }]}
                      >
                        <Input placeholder="请输入客户手机号" autoComplete="off" />
                      </Form.Item>    

                      <Form.Item label="职位" name="position" className="formItem-customer_unrequired" 
                                 rules={[{ required: true, message: '请输入客户职位' }]}
                      >
                        <Input placeholder="请输入客户职位" autoComplete="off" />
                      </Form.Item>

                      <Form.Item label="部门" name="department" className="formItem-customer_unrequired" 
                                 rules={[{ required: true, message: '请输入客户所在部门' }]}
                      >
                        <Input placeholder="请输入客户所在部门" autoComplete="off" />
                      </Form.Item>

                      <Form.Item label="固话" name="phone" className="formItem-customer_unrequired" rules={[{ required: false }]}>
                        <Input placeholder="请输入客户固话号码" autoComplete="off" />
                      </Form.Item>    

                      <Form.Item label="微信号" name="wechat" className="formItem-customer_unrequired" rules={[{ required: false }]}>
                        <Input placeholder="请输入客户微信号" autoComplete="off" />
                      </Form.Item>

                      <Form.Item 
                        label="邮箱" 
                        name="email" 
                        className="formItem-customer_unrequired" 
                        rules={[
                          { 
                            required: false 
                          },
                          {
                            type: 'email',
                            message: '请输入有效的邮箱地址'
                          },
                          {
                            pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
                            message: '邮箱格式不正确'
                          }
                        ]}
                      >
                        <Input placeholder="请输入客户邮箱地址" autoComplete="off" />
                      </Form.Item>

                      <Form.Item label="传真" name="fax" className="formItem-customer_unrequired" rules={[{ required: false }]}>
                        <Input placeholder="请输入客户传真号" autoComplete="off" />
                      </Form.Item>

                      <Form.Item
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            重点
                            <Tooltip title={KEY_CUSTOMER_SELECTOR_HINT_MESSAGE}>
                              <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} />
                            </Tooltip>
                          </div>
                        }
                        name="keyCustomer"
                        className="formItem-customer_selector"
                        rules={[{ required: false }]}
                        
                      >
                        <Select>
                            <Select.Option key={1} value={1}>是</Select.Option>
                            <Select.Option key={0} value={0}>否</Select.Option>
                        </Select>
                      </Form.Item>

                      <Form.Item label="状态" name="states" className="formItem-customer_states" rules={[{ required: true, message: '请选择状态' }]}>                  
                        <StatesSelect 
                          showAllOption={false} 
                          initialValues={this.state.customerData ? this.state.customerData.states : States.normal}
                          selectorType={StatesSelectorType.customer}
                        />
                      </Form.Item>

                      <Form.Item label="备注" name="remark" className="formItem-customer_unrequired" rules={[{ required: false }]}>
                        <Input.TextArea placeholder="请输入备注" />
                      </Form.Item>

                      <Form.Item label="公司" name="company" rules={[{ required: false }]} style={{ display: 'none' }} >
                        {this.state.companyId ? (
                          <Input type="hidden" value={this.state.companyId} />
                        ) : null}
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" className="button-customer_submit" htmlType="submit" loading={this.state.loading} >
                          提交
                        </Button>
                      </Form.Item>
                    </Form>
                </div>   
            </Card>
          </div> 
        </ErrorBoundary> : ""
    ); 
  }
}

export default CustomerManagerPage;